import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Grid,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Avatar,
  IconButton,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { findAddOnProduct } from '../../../../store/addonProduct/api';
import { findAddOnCategory } from '../../../../store/addonCategory/api';
import { AddOnProduct } from '../../../../store/addonProduct/api/interface';
import { getAssetURl } from '../../../../utils/url';
import Iconify from '../../../../components/Iconify';

export interface SelectedAddon {
  id: string;
  addonProductId: string;
  labelEn: string;
  labelAr: string;
  price: number;
  qty: number;
  unitPrice: number;
}

interface AddonSelectorProps {
  selectedAddons: SelectedAddon[];
  onAddonsChange: (addons: SelectedAddon[]) => void;
}

const AddonSelector: React.FC<AddonSelectorProps> = ({ selectedAddons, onAddonsChange }) => {
  const dispatch = useAppDispatch();
  const { data: addonProducts } = useAppSelector((state) => state.addOnProduct);
  const { data: addonCategories } = useAppSelector((state) => state.addOnCategory);
  const assetUrl = useAppSelector((state) => state.active.selectedItem.assetUrl);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Load addon categories and products on mount
  useEffect(() => {
    dispatch(findAddOnCategory({}));
    dispatch(findAddOnProduct({}));
  }, [dispatch]);

  // Filter addon products by category and search
  const filteredAddonProducts = addonProducts.filter((product) => {
    const matchesCategory = !selectedCategoryId || product.addonCategoryId === selectedCategoryId;
    const matchesSearch =
      !searchQuery.trim() ||
      product.labelEn?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.labelAr?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.descriptionEn?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.descriptionAr?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleAddAddon = (product: AddOnProduct) => {
    // Check if addon already exists in the list
    const existingAddon = selectedAddons.find((addon) => addon.addonProductId === product.id);
    
    if (existingAddon) {
      // If exists, increase quantity instead of adding duplicate
      handleQuantityChange(existingAddon.id, existingAddon.qty + 1);
      return;
    }
    
    const price = parseFloat(product.discountedPrice) > 0 ? parseFloat(product.discountedPrice) : parseFloat(product.price);
    const newAddon: SelectedAddon = {
      id: `${product.id}-${Date.now()}`,
      addonProductId: product.id,
      labelEn: product.labelEn,
      labelAr: product.labelAr,
      price: price,
      qty: 1,
      unitPrice: price,
    };
    onAddonsChange([...selectedAddons, newAddon]);
  };

  const handleRemoveAddon = (id: string) => {
    onAddonsChange(selectedAddons.filter((addon) => addon.id !== id));
  };

  const handleQuantityChange = (id: string, newQty: number) => {
    if (newQty < 1) {
      handleRemoveAddon(id);
      return;
    }
    const updatedAddons = selectedAddons.map((addon) => {
      if (addon.id === id) {
        return {
          ...addon,
          qty: newQty,
          unitPrice: addon.price * newQty,
        };
      }
      return addon;
    });
    onAddonsChange(updatedAddons);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  return (
    <Card sx={{ p: 3, mt: 3 }}>
      <Typography variant='h6' gutterBottom>
        Addons
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
        Add additional products to your order
      </Typography>

      <Grid container spacing={3}>
        {/* Category Selection */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Select Category</InputLabel>
            <Select
              value={selectedCategoryId}
              onChange={(e) => setSelectedCategoryId(e.target.value)}
              label='Select Category'
            >
              <MenuItem value=''>All Categories</MenuItem>
              {addonCategories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.labelEn} ({category.labelAr})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Search Bar */}
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            placeholder='Search addons by name or description...'
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                  <Iconify icon='eva:search-fill' sx={{ color: 'text.disabled', width: 20, height: 20 }} />
                </Box>
              ),
              endAdornment: searchQuery && (
                <IconButton size='small' onClick={() => handleSearchChange('')} sx={{ mr: -1 }}>
                  <Iconify icon='eva:close-fill' sx={{ width: 20, height: 20 }} />
                </IconButton>
              ),
            }}
          />
        </Grid>

        {/* Addon Products Grid */}
        <Grid item xs={12}>
          {filteredAddonProducts.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant='body2' color='text.secondary'>
                {searchQuery
                  ? `No addons found matching "${searchQuery}"${selectedCategoryId ? ' in this category' : ''}`
                  : selectedCategoryId
                  ? 'No addons found in this category'
                  : 'No addons available'}
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {filteredAddonProducts.map((product) => {
                const price = parseFloat(product.discountedPrice) > 0 ? parseFloat(product.discountedPrice) : parseFloat(product.price);
                const isSelected = selectedAddons.some((addon) => addon.addonProductId === product.id);
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                    <Card
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        border: isSelected ? '2px solid #EE3750' : '1px solid #e0e0e0',
                        '&:hover': {
                          boxShadow: 3,
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          src={getAssetURl(product.previewUrl, assetUrl)}
                          alt={product.labelEn}
                          sx={{ width: 80, height: 80, mb: 1 }}
                          variant='rounded'
                        />
                        <Typography variant='subtitle2' align='center' sx={{ fontWeight: 'bold' }}>
                          {product.labelEn}
                        </Typography>
                        <Typography variant='caption' align='center' color='text.secondary'>
                          {product.labelAr}
                        </Typography>
                        <Typography variant='body2' align='center' sx={{ fontWeight: 'bold', color: '#EE3750' }}>
                          {price.toFixed(2)} QAR
                        </Typography>
                        {product.descriptionEn && (
                          <Typography variant='caption' align='center' color='text.secondary' sx={{ fontSize: '0.7rem' }}>
                            {product.descriptionEn}
                          </Typography>
                        )}
                        <Button
                          variant={isSelected ? 'outlined' : 'contained'}
                          size='small'
                          startIcon={<AddIcon />}
                          onClick={() => handleAddAddon(product)}
                          disabled={isSelected}
                          sx={{
                            mt: 1,
                            backgroundColor: isSelected ? 'transparent' : '#EE3750',
                            '&:hover': {
                              backgroundColor: isSelected ? 'transparent' : '#EE3750',
                            },
                          }}
                        >
                          {isSelected ? 'Added' : 'Add'}
                        </Button>
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Grid>
      </Grid>
    </Card>
  );
};

export default AddonSelector;

